import {
  IonCol,
  IonGrid,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonRow,
  IonText,
  IonTitle,
} from "@ionic/react";
import { useCallback, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { decodeParam } from "../util/ApiHelper";
import "./ResourcesTab.css";
import "./TableListItem.css";
import { getWorkOrder } from "../api/WorkOrders";
import { TranslationMessagesContext } from "../util/Translations";
import { formatCurrencyUICurrency } from "../util/UIHelper";
import { getCurrency } from "../util/PlantHelper";
import { useWorkOrderStore } from "../store/wo-order";
import { useShallow } from "zustand/react/shallow";
import useGetResourcesFields from "../hooks/work-order/useGetResourcesFields";
import { reactPlugin } from "../util/AppInsights";
import { withAITracking } from "@microsoft/applicationinsights-react-js";

const ResourcesTab: React.FC = () => {
  const { workOrderId } = useParams<{ workOrderId: string }>();
  let displayWo = decodeParam(workOrderId);

  const { translatedMessages } = useContext(TranslationMessagesContext);
  const [fieldsErrorState, setFieldsErrorState] = useState(false);
  const [resourcesErrorState, setResourcesErrorState] = useState(false);

  const [showLoading, setShowLoading] = useState(false);

  const resourceFields = useWorkOrderStore(
    useShallow((state) => state.getResourcesFieldsForWo(displayWo))
  );
  const workOrder = useWorkOrderStore(
    useShallow((state) => state.getWorkOrderById(displayWo))
  );
  const { getResourcesFields: triggerGetResourcesFields } =
    useGetResourcesFields();
  const addUpdateWorkOrder = useWorkOrderStore(
    (state) => state.addUpdateWorkOrder
  );
  const currency = useWorkOrderStore((state) =>
    state.getPlantCurrencyForWo(displayWo)
  );
  const setCurrency = useWorkOrderStore((state) => state.setWoPlantCurrency);

  const resources = workOrder?.Resources ?? [];

  const getResourcesFields = useCallback(async () => {
    if (resourceFields) return; //got it from cache

    const response = await triggerGetResourcesFields(displayWo);
    if (response.status !== 200) {
      setFieldsErrorState(true);
    } else if (response.data) {
      setFieldsErrorState(false);
    }
  }, [displayWo, resourceFields, triggerGetResourcesFields]);

  const getWorkOrderData = useCallback(async () => {
    if (workOrder) return; //got it from cache
    const response = await getWorkOrder({
      woNumber: displayWo,
      resources: true,
      appName: "Mobile",
      translations: true,
      equipmentMaster: true,
      permits: true,
      comment: true,
      events: true,
      inspectionDefinition: false,
      labor: true,
    });
    if (response.status !== 200) {
      setResourcesErrorState(true);
    } else if (response.data) {
      addUpdateWorkOrder(response.data);
      setResourcesErrorState(false);
    }
  }, [addUpdateWorkOrder, displayWo, workOrder]);

  const getPlantCurrency = useCallback(async () => {
    const curr = await getCurrency();
    setCurrency(displayWo, curr);
  }, [displayWo, setCurrency]);

  useEffect(() => {
    (async () => {
      setFieldsErrorState(false);
      // Show loading symbol
      setShowLoading(true);
      await getResourcesFields();
      await getWorkOrderData();
      await getPlantCurrency();
      setShowLoading(false);
    })();
  }, [getPlantCurrency, getResourcesFields, getWorkOrderData, workOrderId]);

  const createGridColumn = (resource: any, field: any) => {
    let label = field.TranslatedIdText;
    let value = resource[field.PropertyName];
    let format = field.FormatId;

    return (
      <IonCol size="6" key={`resource-field-${field.IdText}-col`}>
        <IonItem
          class="list-item-grid-col-item"
          lines="none"
          key={`resource-field-${field.IdText}`}
        >
          <IonLabel className="list-item-field-label">{label}</IonLabel>
          <IonLabel className="list-item-field-value">
            {format === "currency"
              ? formatCurrencyUICurrency(Number(value), currency)
              : value}
          </IonLabel>
        </IonItem>
      </IonCol>
    );
  };

  return (
    <>
      <IonLoading
        isOpen={showLoading}
        onDidDismiss={() => setShowLoading(false)}
        duration={5000}
      />
      {!!resources && resources.length > 0 ? (
        <IonList lines="full" class="table-list">
          {resources.map((resource: any, index) => {
            return (
              <IonItem key={index} class="list-item">
                <IonGrid class="list-item-grid">
                  {(resourceFields ?? []).map((field, index, arr) => {
                    if (Number(field.FieldOrder) === 1) {
                      return (
                        <IonRow class="table-list-item-row">
                          <IonCol size="12">
                            <IonItem
                              lines="none"
                              class="resource-item-grid-col-title-item"
                            >
                              <IonTitle class="resource-item-title">
                                {resource[field.PropertyName]}
                              </IonTitle>
                            </IonItem>
                          </IonCol>
                        </IonRow>
                      );
                    } else if (index % 2 === 0) {
                      // Skip the even numbers to avoid duplicate columns
                      return <></>;
                    }
                    return (
                      <IonRow class="table-list-item-row">
                        {createGridColumn(resource, field)}
                        {index + 1 !== arr.length &&
                          createGridColumn(resource, arr[index + 1])}
                      </IonRow>
                    );
                  })}
                </IonGrid>
              </IonItem>
            );
          })}
        </IonList>
      ) : (
        <IonText
          color={fieldsErrorState || resourcesErrorState ? "danger" : "default"}
          style={{ padding: "16px", display: "inline-block" }}
          hidden={showLoading}
        >
          {fieldsErrorState || resourcesErrorState ? (
            <span>
              {fieldsErrorState ? (
                <p>
                  {translatedMessages["ErrFetchingRes"]?.MessageText ||
                    "Error fetching the resource table fields"}
                </p>
              ) : (
                ""
              )}
              {resourcesErrorState ? (
                <p>
                  {translatedMessages["ErrFetchingWORes"]?.MessageText ||
                    "Error fetching the resources associated with this work order"}
                </p>
              ) : (
                ""
              )}
            </span>
          ) : (
            translatedMessages["NoResultsFound"]?.MessageText ||
            "No results found."
          )}
        </IonText>
      )}
    </>
  );
};

export default withAITracking(reactPlugin, ResourcesTab, "ResourcesTab");
